import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, ShieldCheck, XCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fdate, money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function RepairApproval() {
  const { token } = useParams();
  const [est, setEst] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");

  useEffect(() => {
    api.get(`/public/repair-estimate/${token}`)
      .then((r) => { setEst(r.data); setName(r.data.customer_name || ""); if (r.data.decided) setDone(r.data.approval?.decision); })
      .catch((e) => setError(errMsg(e, "This estimate link is not valid any more.")));
  }, [token]);

  const decide = async (approve) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/repair-estimate/${token}`, { approve, remarks, name });
      setDone(data.status);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="grid min-h-screen place-items-center bg-background px-4">
        <p className="text-center text-sm text-muted-foreground" data-testid="est-error">{error}</p>
      </div>
    );
  }
  if (!est) {
    return <div className="grid min-h-screen place-items-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  const pf = est.print_fields || {};
  return (
    <div className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-2xl space-y-5">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-lg bg-gradient-warm text-primary-foreground">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div>
            <p className="font-display text-lg font-bold">{est.company?.name || "Megatronix Vision"}</p>
            <p className="text-xs text-muted-foreground">Repair estimate {est.number}{est.rev ? ` Rev ${est.rev}` : ""}</p>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 shadow-xs" data-testid="est-card">
          <p className="text-sm">Dear <b>{est.customer_name}</b>, here is our estimate for the repair of your equipment.</p>
          <div className="mt-4 space-y-1 text-sm">
            <p><span className="text-muted-foreground">Equipment: </span><b>{est.equipment}</b> {est.model_no ? `(${est.model_no})` : ""}</p>
            {pf.serial_no !== false && est.serial_no && <p><span className="text-muted-foreground">Serial: </span><span className="num">{est.serial_no}</span></p>}
            <p><span className="text-muted-foreground">Complaint: </span>{est.complaint}</p>
            {pf.diagnosis !== false && est.diagnosis && <p><span className="text-muted-foreground">Diagnosis: </span>{est.diagnosis}</p>}
            {pf.expected_delivery !== false && est.expected_delivery && <p><span className="text-muted-foreground">Expected delivery: </span>{fdate(est.expected_delivery)}</p>}
            {pf.warranty !== false && <p><span className="text-muted-foreground">Repair warranty: </span>{est.warranty_months} month(s)</p>}
            <p><span className="text-muted-foreground">Valid until: </span>{fdate(est.valid_until)}</p>
          </div>

          <table className="mt-4 w-full text-sm">
            <tbody>
              {pf.parts !== false && (est.parts || []).map((p, i) => (
                <tr key={`p${i}`} className="border-b border-border/60">
                  <td className="py-2">{p.name} <span className="text-xs text-muted-foreground">x {p.qty}</span></td>
                  <td className="num py-2 text-right">{money(p.qty * p.price)}</td>
                </tr>
              ))}
              {pf.labour !== false && (est.labour || []).map((l, i) => (
                <tr key={`l${i}`} className="border-b border-border/60">
                  <td className="py-2">{l.description} <span className="text-xs text-muted-foreground">({l.hours} hr)</span></td>
                  <td className="num py-2 text-right">{money(l.hours * l.rate)}</td>
                </tr>
              ))}
              {pf.other_costs !== false && (est.other_costs || []).map((o, i) => (
                <tr key={`o${i}`} className="border-b border-border/60">
                  <td className="py-2">{o.description}</td>
                  <td className="num py-2 text-right">{money(o.amount)}</td>
                </tr>
              ))}
              <tr><td className="pt-3 text-muted-foreground">Subtotal</td><td className="num pt-3 text-right">{money(est.subtotal)}</td></tr>
              <tr><td className="text-muted-foreground">GST</td><td className="num text-right">{money(est.tax_total)}</td></tr>
              <tr><td className="pt-2 font-semibold">Total estimate</td><td className="num pt-2 text-right text-lg font-bold text-primary" data-testid="est-total">{money(est.total)}</td></tr>
              {pf.advance !== false && Number(est.advance_amount) > 0 && (
                <tr><td className="text-muted-foreground">Advance payable</td><td className="num text-right">{money(est.advance_amount)}</td></tr>
              )}
            </tbody>
          </table>

          {pf.inspection !== false && est.inspection && (
            <p className="mt-3 text-sm"><span className="text-muted-foreground">Inspection findings: </span>{est.inspection}</p>
          )}
          {pf.notes !== false && est.notes && (
            <p className="mt-3 whitespace-pre-line text-sm" data-testid="est-notes">
              <span className="text-muted-foreground">Notes: </span>{est.notes}
            </p>
          )}
          {pf.terms !== false && est.terms && (
            <p className="mt-4 whitespace-pre-line text-[11px] leading-relaxed text-muted-foreground">{est.terms}</p>
          )}
          {pf.bank !== false && est.bank?.bank_name && (
            <div className="mt-4 rounded-lg border border-border bg-muted/40 p-3 text-xs" data-testid="est-bank">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Bank details</p>
              <p>{est.bank.account_name}</p>
              <p className="num">A/C {est.bank.account_number} &middot; IFSC {est.bank.ifsc}</p>
              <p>{est.bank.bank_name}{est.bank.branch ? `, ${est.bank.branch}` : ""}</p>
            </div>
          )}
        </div>

        {done ? (
          <div className="rounded-2xl border border-border bg-card p-6 text-center" data-testid="est-done">
            {done === "Approved"
              ? <CheckCircle2 className="mx-auto h-10 w-10 text-success" />
              : <XCircle className="mx-auto h-10 w-10 text-destructive" />}
            <p className="mt-3 font-display text-lg font-bold">Estimate {done.toLowerCase()}</p>
            <p className="text-sm text-muted-foreground">
              Thank you. Our service team has been informed and will contact you.
            </p>
          </div>
        ) : (
          <div className="space-y-3 rounded-2xl border border-border bg-card p-5 shadow-xs">
            <p className="font-display text-base font-semibold">Do we go ahead?</p>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
              data-testid="est-name" />
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
              placeholder="Anything you want us to note (optional)" data-testid="est-remarks" />
            <div className="flex flex-wrap gap-2">
              <Button variant="success" disabled={busy} onClick={() => decide(true)} data-testid="est-approve">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Approve the repair
              </Button>
              <Button variant="outline" className="text-destructive" disabled={busy}
                onClick={() => decide(false)} data-testid="est-reject">
                <XCircle className="h-4 w-4" /> Not now
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
